import { updateCompanion } from '../services/companionService';
import { applyUnshakable } from '../utils/stressCalculations';
import { dropForm } from '../services/beastformService';
import { processReactiveDamageEffects } from '../config/mechanicsConfig';
import { hasLoadoutCard } from '../utils/cardFeatureDetection';
import { hasSubclass, hasSubclassFeature } from '../utils/featureDetection';
import toast from 'react-hot-toast';

/**
 * Check if character has Rise Up domain card in loadout
 * @param {Object} character - Character object
 * @returns {boolean} True if Rise Up is in loadout
 */
const hasRiseUp = (character) => hasLoadoutCard(character, 'rise-up');

/**
 * Check if character has On the Brink domain card in loadout
 * @param {Object} character - Character object
 * @returns {boolean} True if On the Brink is in loadout
 */
const hasOnTheBrink = (character) => hasLoadoutCard(character, 'on-the-brink');

/**
 * Check if character has a specific subclass feature (with explicit subclass check)
 * This is a specialized version that also verifies the character has the expected subclass.
 * Use hasSubclass() + hasSubclassFeature() from featureDetection.js for simpler checks.
 *
 * @param {Object} character - Character object
 * @param {string} subclassId - Subclass ID to check (e.g., 'stalwart', 'vengeance')
 * @param {string} feature - Feature name to check
 * @param {string} tier - Feature tier (foundation, specializations, mastery)
 * @returns {boolean} True if character has the subclass AND the feature
 */
const hasSubclassFeatureWithSubclass = (character, subclassId, feature, tier = null) => {
  if (!hasSubclass(character, subclassId)) return false;
  const features = character.subclassFeatures || {};

  if (tier) {
    // Check specific tier
    let tierFeatures;
    if (tier === 'specializations' || tier === 'specialization') {
      tierFeatures = features.specializations || [];
    } else if (tier === 'mastery' || tier === 'masteries') {
      tierFeatures = features.masteries || [];
    } else {
      tierFeatures = features[tier] || [];
    }
    return Array.isArray(tierFeatures)
      ? tierFeatures.includes(feature)
      : tierFeatures === feature;
  }

  // Check all tiers
  const allFeatures = [
    ...(features.foundation ? [features.foundation] : []),
    ...(features.specializations || []),
    ...(features.masteries || [])
  ].flat();
  return allFeatures.includes(feature);
};

// Debounce tracker for Rise Up stress clear (prevents multiple clears in quick succession)
let riseUpDebounceTimer = null;
let riseUpDebounced = false;

/**
 * Custom hook for managing character vitals (HP, Stress, Hope, Armor, Conditions)
 * Also handles companion vitals (HP, Stress)
 *
 * @param {Object} character - Current character object
 * @param {Function} setCharacter - Function to update local character state
 * @param {Function} onCharacterUpdate - Firebase update callback
 * @param {Function} setIsFacingDeath - Function to trigger death modal
 * @param {Object} hpDamageMode - Current HP damage mode (from useHPDamageMode)
 * @param {Function} handleHPDamageWithMode - HP damage handler (from useHPDamageMode)
 * @param {Function} getSpellcastTrait - Function to get spellcast trait
 * @param {Object} allData - Game data
 * @param {Object} companion - Companion object (optional)
 * @param {Function} setCompanion - Function to update companion state (optional)
 * @param {Object} user - Current user object for companion updates (optional)
 * @param {Object} characterRef - Ref to always-current character (optional, prevents stale state issues)
 * @param {boolean} beetlesActive - Whether Conjure Swarm beetles are active (optional)
 * @param {Function} setBeetlesActive - Function to deactivate beetles (optional)
 * @param {Function} setShowBeetlesMaintainOption - Function to show inline "spend Hope to maintain" option (optional)
 * @returns {Object} Vitals management functions
 */
const useVitalsManager = (
  character,
  setCharacter,
  onCharacterUpdate,
  setIsFacingDeath,
  hpDamageMode,
  handleHPDamageWithMode,
  getSpellcastTrait,
  allData,
  companion,
  setCompanion,
  user,
  characterRef,
  beetlesActive = false,
  setBeetlesActive = null,
  setShowBeetlesMaintainOption = null
) => {

  const updateVitals = async (key, value, targetCompanion) => {
    // Handle companion stress (companions only have stress, no HP)
    if (key === 'companionStress') {
      if (!targetCompanion || !user) {
        console.error('Companion or user not provided for companion stress update');
        return;
      }

      let newValue = Math.max(0, value || 0);
      const maxStress = targetCompanion.maxStress || 4;

      // Clamp to max (companions don't have stress overflow - they just drop out)
      newValue = Math.min(newValue, maxStress);

      // Update companion stress in Firestore
      try {
        await updateCompanion(character.id, targetCompanion.id, {
          stress: newValue
        });
        setCompanion({ ...targetCompanion, stress: newValue });
      } catch (error) {
        console.error('Error updating companion stress:', error);
      }
      return;
    }

    // Handle character vitals (original logic)
    // Use characterRef if available to get the latest state (prevents stale closure issues)
    const currentCharacter = characterRef?.current || character;
    let newValue = Math.max(0, value || 0);
    let updatedCharacter = { ...currentCharacter };
    let hpDamageTriggered = false;
    let hpDamageAmount = 0;

    if (key === 'hp') {
      newValue = Math.min(newValue, currentCharacter.maxHp || 8);
      const oldHP = currentCharacter.hp || 0;

      // Check if HP decreased (damage taken)
      if (newValue < oldHP) {
        hpDamageAmount = oldHP - newValue;
        hpDamageTriggered = true;

        // Conjure Swarm - Beetles threshold reduction
        // When beetles are active, reduce damage severity by 1 threshold (minimum 0)
        if (beetlesActive && setBeetlesActive && hpDamageAmount > 0) {
          const reducedDamage = Math.max(0, hpDamageAmount - 1);
          const damageReduced = hpDamageAmount - reducedDamage;

          if (damageReduced > 0) {
            // Apply reduced damage instead
            newValue = oldHP - reducedDamage;
            hpDamageAmount = reducedDamage;

            // Show notification
            if (hpDamageAmount === 0) {
              toast.success('🪲 Armored Beetles! Damage completely blocked!', { duration: 3500 });
            } else {
              toast.success(`🪲 Armored Beetles! Damage reduced by 1 (${hpDamageAmount + damageReduced} → ${hpDamageAmount} HP)`, { duration: 3500 });
            }

            // Show inline "spend Hope to maintain" option in StickyVitalsBar
            if (setShowBeetlesMaintainOption) {
              setTimeout(() => setShowBeetlesMaintainOption(true), 500);
            } else {
              // No maintain option configured - just deactivate beetles
              setBeetlesActive(false);
              toast('🪲 Beetles dispersed.', { duration: 2000 });
            }
          }
        }

        // On the Brink (BONE): Ignore Minor damage when 2 or fewer HP remain
        // SRD: "When you have 2 or fewer Hit Points unmarked, you don't take Minor damage."
        // Minor damage = 1 HP in Daggerheart
        if (hasOnTheBrink(currentCharacter) && hpDamageAmount === 1) {
          const unmarkedHP = (currentCharacter.maxHp || 8) - oldHP;
          // "Unmarked" means remaining/available HP slots (not yet marked as damage)
          // So if HP = 6 and maxHP = 8, unmarkedHP = 2 (we have 2 unmarked HP slots left)
          // Wait - that's wrong. "Unmarked HP" = current HP (how many HP you still have)
          // So we check if current HP (before damage) <= 2
          if (oldHP <= 2) {
            // Block Minor damage - restore HP to original value
            newValue = oldHP;
            hpDamageAmount = 0;
            hpDamageTriggered = false;
            toast.success('💀 On the Brink! Minor damage ignored!', { duration: 3000 });
          }
        }

        // Earth Dominion (Warden of the Elements mastery): Roll d6 per HP, reduce by 1 per 6
        // SRD: "When you would mark Hit Points, roll a d6 per Hit Point marked. For each result of 6, reduce the number of Hit Points you mark by 1."
        // Requires: channeling Earth element + has Elemental Dominion mastery
        if (hasSubclassFeatureWithSubclass(currentCharacter, 'warden-of-the-elements', 'Elemental Dominion', 'mastery') &&
            currentCharacter.isChanneling &&
            currentCharacter.channelingElement === 'earth' &&
            hpDamageAmount > 0) {
          // Roll d6 for each HP that would be marked
          const d6Rolls = [];
          for (let i = 0; i < hpDamageAmount; i++) {
            d6Rolls.push(Math.floor(Math.random() * 6) + 1);
          }
          // Count sixes (reduce damage by 1 per 6)
          const sixes = d6Rolls.filter(roll => roll === 6);
          const reduction = sixes.length;

          if (reduction > 0) {
            const reducedDamage = Math.max(0, hpDamageAmount - reduction);
            newValue = oldHP - reducedDamage;
            toast.success(`🪨 Earth Dominion! Rolled [${d6Rolls.join(', ')}] - ${sixes.length} sixes! Damage reduced: ${hpDamageAmount}→${reducedDamage} HP`, { duration: 4000 });
            hpDamageAmount = reducedDamage;
          } else {
            toast(`🪨 Earth Dominion: Rolled [${d6Rolls.join(', ')}] - no 6s (${hpDamageAmount} HP)`, { duration: 3000 });
          }
        }

        // Rise Up (VALOR): Clear 1 Stress when marking HP from an attack
        // SRD: "When you mark 1 or more Hit Points from an attack, clear a Stress."
        // Uses debounce to handle multiple HP marks in quick succession (single "round" of damage)
        if (hasRiseUp(currentCharacter) && hpDamageAmount > 0) {
          const currentStress = currentCharacter.stress || 0;

          // Only trigger if we have stress to clear and aren't already debounced
          if (currentStress > 0 && !riseUpDebounced) {
            riseUpDebounced = true;

            // Clear the debounce after 500ms to allow next damage instance
            if (riseUpDebounceTimer) clearTimeout(riseUpDebounceTimer);
            riseUpDebounceTimer = setTimeout(() => {
              riseUpDebounced = false;
            }, 500);

            // Clear 1 stress (will be included in the character update)
            updatedCharacter.stress = Math.max(0, currentStress - 1);
            toast.success('⬆️ Rise Up: Cleared 1 Stress!', { duration: 2500 });
          }
        }

        // Rogue's Dodge: Clear on taking damage (hit detection)
        // SRD: "+2 Evasion until you are hit or rest"
        // Only clear if actual HP damage was dealt (not blocked by Beetles, On the Brink, etc.)
        if (currentCharacter.roguesDodgeActive && hpDamageAmount > 0) {
          updatedCharacter.roguesDodgeActive = false;
          toast.success('🗡️ Rogue\'s Dodge ended (hit)', { duration: 2000 });
        }

        // === BEASTFORM REACTIVE EFFECTS ===

        // Beastform Fragile: Drop out when taking Major damage (2+ HP)
        // SRD: "When you take Major or greater damage, you drop out of Beastform."
        if (currentCharacter.activeBeastform && hpDamageAmount >= 2) {
          const hasFragile = currentCharacter.activeBeastform.features?.some(
            f => f.mechanic === 'dropOnMajorDamage'
          );
          if (hasFragile) {
            const formName = currentCharacter.activeBeastform.formName;
            updatedCharacter.activeBeastform = null;
            toast.error(`💔 ${formName} dropped from Major damage (Fragile)!`, { duration: 4000 });
          }
        }

        // === SUBCLASS REACTIVE EFFECTS ===

        // Stalwart "Iron Will" (Foundation): Mark armor slot to reduce severity
        // SRD: "Mark an Armor Slot to reduce physical damage severity by one"
        if (hasSubclassFeatureWithSubclass(currentCharacter, 'stalwart', 'Iron Will', 'foundation') && hpDamageAmount > 0) {
          const currentArmorMarks = currentCharacter.armorMarks || [];
          const totalArmor = currentCharacter.armor || 0;
          const unmarkedSlots = totalArmor - currentArmorMarks.length;
          if (unmarkedSlots > 0) {
            toast('🛡️ Iron Will: Mark Armor Slot to reduce damage severity!', {
              duration: 4000,
              icon: '⚔️'
            });
          }
        }

        // Vengeance "Revenge" (Foundation): Reactive when hit in melee
        // SRD: "When an adversary hits you with a Melee attack, you can mark 2 Stress to deal 1 HP to attacker"
        if (hasSubclassFeatureWithSubclass(currentCharacter, 'vengeance', 'Revenge', 'foundation') && hpDamageAmount > 0) {
          const currentStress = currentCharacter.stress || 0;
          const maxStress = currentCharacter.maxStress || 8;
          if (currentStress + 2 <= maxStress) {
            toast('💢 Revenge: Mark 2 Stress to deal 1 HP to attacker (Melee only)!', {
              duration: 4000,
              icon: '⚔️'
            });
          }
        }

        // Process config-driven reactive damage effects (Warden of the Elements, etc.)
        // Config: REACTIVE_DAMAGE_EFFECTS in mechanicsConfig.js
        const triggeredEffects = processReactiveDamageEffects(
          currentCharacter,
          hpDamageAmount,
          hasSubclassFeature
        );
        for (const effect of triggeredEffects) {
          toast.success(`${effect.icon} ${effect.message}`, {
            duration: 4000,
            icon: effect.icon
          });
        }

      }

      updatedCharacter.hp = newValue;
    } else if (key === 'hope') {
      // Hope is limited by available (non-scarred) slots
      const availableHopeSlots = Math.max(0, 6 - (currentCharacter.scars?.length || 0));
      newValue = Math.min(newValue, availableHopeSlots);
      updatedCharacter.hope = newValue;
    } else if (key === 'stress') {
      const maxStress = currentCharacter.maxStress || 8;
      const currentStress = currentCharacter.stress || 0;

      // Apply Firbolg Unshakable if stress is increasing
      const actualNewValue = applyUnshakable(currentStress, newValue, currentCharacter, (roll, avoided) => {
        if (avoided) {
          toast.success(`🛡️ Unshakable! Rolled ${roll} - Stress avoided`, { duration: 2500, icon: '🎲' });
        } else {
          toast.info(`Unshakable: Rolled ${roll} - Stress marked`, { duration: 1500 });
        }
      });

      if (actualNewValue > maxStress) {
        // Stress overflow: excess stress becomes HP damage
        const overflow = actualNewValue - maxStress;
        updatedCharacter.stress = maxStress;
        const oldHP = currentCharacter.hp || 0;
        updatedCharacter.hp = Math.max(0, oldHP - overflow);

        // Trigger HP damage from stress overflow
        if (overflow > 0) {
          hpDamageAmount = overflow;
          hpDamageTriggered = true;
        }
      } else {
        updatedCharacter.stress = actualNewValue;
      }
    } else if (key === 'armor') {
      updatedCharacter.armor = Math.max(0, value || 0); // armor can go higher
    } else if (key === 'slayerDice') {
      // Slayer Dice is an array, not a number
      updatedCharacter.slayerDice = value;
    } else {
      updatedCharacter[key] = newValue;
    }

    setCharacter(updatedCharacter);

    // Check for HP-triggered domain cards with active mode
    if (hpDamageTriggered && hpDamageAmount > 0 && hpDamageMode) {
      updatedCharacter = await handleHPDamageWithMode(hpDamageAmount, updatedCharacter, getSpellcastTrait, allData);
      setCharacter(updatedCharacter);
    }

    // Beastform: Auto-drop when marking last HP (0 HP remaining)
    // SRD: "If you mark your last Hit Point while in a Beastform, you automatically drop out"
    if (updatedCharacter.hp === 0 && updatedCharacter.activeBeastform) {
      const formName = updatedCharacter.activeBeastform.formName;
      updatedCharacter.activeBeastform = null;
      toast.error(`💔 Dropped out of ${formName} at 0 HP!`, { duration: 4000 });
    }

    // Trigger death move if HP reaches 0 and character is not already dead/journey ended
    if (updatedCharacter.hp === 0 && currentCharacter.status !== 'deceased' && currentCharacter.status !== 'journey_ended') {
      setTimeout(() => {
        setIsFacingDeath(true);
      }, 500); // Small delay to let HP update visually first
    }

    // Use the callback to persist to Firebase
    if (onCharacterUpdate) {
      try {
        await onCharacterUpdate(updatedCharacter);
      } catch (error) {
        console.error('Error updating character vitals:', error);
      }
    }
  };

  const markArmor = (index) => {
    // Use characterRef if available to get the latest state
    const currentCharacter = characterRef?.current || character;
    const newMarks = [...(currentCharacter.armorMarks || [])];
    const markIndex = newMarks.indexOf(index);
    if (markIndex > -1) newMarks.splice(markIndex, 1);
    else newMarks.push(index);

    const updatedCharacter = { ...currentCharacter, armorMarks: newMarks };
    setCharacter(updatedCharacter);

    // Persist the armor marks
    if (onCharacterUpdate) {
      onCharacterUpdate(updatedCharacter);
    }
  };

  const toggleCondition = (condition) => {
    // Use characterRef if available to get the latest state
    const currentCharacter = characterRef?.current || character;
    const newConditions = [...(currentCharacter.conditions || [])];
    const index = newConditions.indexOf(condition);
    if (index > -1) newConditions.splice(index, 1);
    else newConditions.push(condition);

    const updatedCharacter = { ...currentCharacter, conditions: newConditions };
    setCharacter(updatedCharacter);

    // Persist the conditions
    if (onCharacterUpdate) {
      onCharacterUpdate(updatedCharacter);
    }
  };

  return {
    updateVitals,
    markArmor,
    toggleCondition
  };
};

export default useVitalsManager;
